import { AdmissionRules, CarePlanRules } from '@quromedical/business-rules';
import {
  type EncounterDischargeReasonCode,
  type EncounterInvalidateReasonCode,
  GroupSecurity,
} from '@quromedical/fhir-common';
import type { AdmissionGetResponse } from '@quromedical/models';
import { first } from '@quromedical/utils';
import { ConfirmationDialog } from 'components/input';
import {
  AdmissionCard,
  AdmissionDischargeReason,
  type AdmissionDischargeReasonProps,
  AdmissionInlineUpdateCard,
  AdmissionInvalidateReason,
  type AdmissionInvalidateReasonProps,
  AdmissionStart,
  type AdmissionStartProps,
  CreateAdmissionCarePlan,
  type CreateAdmissionCarePlanProps,
} from 'core/admission';
import { WithGroup } from 'core/auth';
import {
  Button,
  Section,
  Skeleton,
  SkeletonProvider,
  Spacer,
  Visible,
} from 'design-system';
import { BASE_SIZE } from 'design-system/theme';
import { router } from 'expo-router';
import { noOpAsync } from 'helpers';
import { useClinicalSummary, useUserSession } from 'hooks';
import { useRevalidation } from 'hooks/useRevalidation';
import { useToggle } from 'hooks/useToggle';
import {
  AdmissionApi,
  CarePlanApi,
  PatientApi,
  PatientEndpoint,
} from 'integration/aggregate';
import React, { useCallback, useMemo, useState } from 'react';
import { strings } from 'strings';
import useSWR from 'swr';

import { ClinicalSummaryCard } from './ClinicalSummaryCard';

type AdmissionActionType =
  | 'discharge'
  | 'invalidate'
  | 'createCarePlan'
  | 'startAdmission';

type AdmissionDischarge = {
  statusReason: EncounterDischargeReasonCode;
  periodEnd: string;
};
type AdmissionInvalidate = {
  admissionId: string;
  statusReason: EncounterInvalidateReasonCode;
};
type AdmissionCarePlanCreate = { planDefinition: string; admissionId: string };
type AdmissionStartData = { admissionId: string; periodStart: string };

interface AdmissionAction<T extends AdmissionActionType> {
  type: T;
  data: T extends 'discharge'
    ? AdmissionDischarge
    : T extends 'invalidate'
      ? AdmissionInvalidate
      : T extends 'createCarePlan'
        ? AdmissionCarePlanCreate
        : AdmissionStartData;
}

interface PatientAdmissionsTabProps {
  patientId: string;
}

const admissionApi = new AdmissionApi();
const patientApi = new PatientApi();
const carePlanApi = new CarePlanApi();

const Placeholder: React.FC<PatientAdmissionsTabProps> = ({ patientId }) => (
  <Section>
    <Skeleton>
      <AdmissionCard downloadFile={noOpAsync} patientId={patientId} />
    </Skeleton>
  </Section>
);

/**
 * Admissions are only editable if they are `in-progress`
 */
const isEditableAdmission = (admission: AdmissionGetResponse) =>
  admission.status === 'in-progress' ||
  admission.status === 'onleave' ||
  admission.status === 'planned';

interface AdmissionListProps {
  admissions: AdmissionGetResponse[];
  patientId: string;
  canEditEncounter: boolean;
  canViewMedia: boolean;
  revalidate: () => Promise<void>;
  onAction: (type: 'discharge' | 'invalidate', admissionId: string) => void;
  canInvalidate: boolean;
}

const AdmissionList: React.FC<AdmissionListProps> = ({
  admissions,
  patientId,
  canEditEncounter,
  canViewMedia,
  revalidate,
  onAction,
  canInvalidate,
}) => {
  return admissions.map((admission, index) => {
    const isEditable = isEditableAdmission(admission);
    const canEdit = canEditEncounter && isEditable;

    const pauseRule = AdmissionRules.AdmissionRules.validateAdmissionRequest({
      activeAdmissions: admissions,
      admissionType: 'pause-admission',
    });
    const resumeRule = AdmissionRules.AdmissionRules.validateAdmissionRequest({
      activeAdmissions: admissions,
      admissionType: 'resume-admission',
    });

    const startRule = AdmissionRules.AdmissionRules.validateAdmissionRequest({
      activeAdmissions: admissions,
      newAdmission: admission,
      admissionType: 'start-planned-admission',
    });

    const showPauseButton =
      admission.status === 'in-progress' && pauseRule.type === 'success';
    const showResumeButton =
      admission.status === 'onleave' && resumeRule.type === 'success';
    const showStartButton =
      admission.status === 'planned' && startRule.type === 'success';

    return (
      <Section
        key={admission.id}
        hasBottomMargin={index === admissions.length - 1}
      >
        <Skeleton>
          <AdmissionInlineUpdateCard
            patientId={patientId}
            revalidate={revalidate}
            data={admission}
            canEdit={canEdit}
            canViewMedia={canViewMedia}
            onTertiarySubmit={(admissionId) =>
              onAction(canInvalidate ? 'invalidate' : 'discharge', admissionId)
            }
            tertiaryButtonText={
              canInvalidate
                ? strings.AdmissionInvalidate
                : strings.PatientDischargeDialogButtonText
            }
            showSecondarySubmitButton={
              canEdit &&
              (showPauseButton || showResumeButton || showStartButton)
            }
          />
        </Skeleton>
      </Section>
    );
  });
};

const getInitialActionData = (
  type: AdmissionActionType,
  admissionId?: string,
) => {
  const initialActionDataMap: {
    discharge: AdmissionDischarge;
    invalidate: AdmissionInvalidate;
    createCarePlan: AdmissionCarePlanCreate;
    startAdmission: AdmissionStartData;
  } = {
    discharge: {
      statusReason: '' as EncounterDischargeReasonCode,
      periodEnd: '',
    },
    invalidate: {
      admissionId: admissionId || '',
      statusReason: '' as EncounterInvalidateReasonCode,
    },
    createCarePlan: {
      planDefinition: '',
      admissionId: admissionId || '',
    },
    startAdmission: {
      admissionId: admissionId || '',
      periodStart: '',
    },
  };
  return initialActionDataMap[type];
};

export const PatientAdmissionsTab: React.FC<PatientAdmissionsTabProps> = ({
  patientId,
}) => {
  const session = useUserSession();
  const revalidate = useRevalidation(patientId);

  const canViewMedia = session.hasAny([GroupSecurity.MediaRead]);
  const canEditEncounter = session.hasAny([GroupSecurity.EncounterUpdate]);
  const canViewClinicalSummary = session.hasAny([
    GroupSecurity.ClinicalImpressionSummary,
  ]);

  const onCreatePress = useCallback(() => {
    router.push({ pathname: `/patient/${patientId}/clinical/admit` });
  }, [patientId]);

  const admissionFetcher = () =>
    admissionApi.getAdmissions({ patient: patientId });
  const carePlanFetcher = () => carePlanApi.getCarePlans({ patientId });

  const swrKey = patientApi.getPatientSWRKey(patientId, PatientEndpoint.Admit);

  const { data: admissionData, isValidating } = useSWR(
    swrKey,
    admissionFetcher,
  );
  const { data: carePlanData } = useSWR(
    `${patientId}-CarePlan`,
    carePlanFetcher,
  );

  const [isDialogOpen, setDialogOpen] = useToggle(false);
  const [currentAction, setCurrentAction] =
    useState<AdmissionAction<AdmissionActionType>>();

  const admissions = useMemo(
    () => admissionData?.admissions || [],
    [admissionData?.admissions],
  );
  const carePlans = useMemo(
    () => carePlanData?.carePlans || [],
    [carePlanData?.carePlans],
  );

  const activeAdmission = useMemo(
    () => admissions.find((admission) => admission.status === 'in-progress'),
    [admissions],
  );

  const lastAdmissionId = first(admissions)?.id;
  const { clinicalSummary, isLoading: isLoadingClinicalSummary } =
    useClinicalSummary(patientId, lastAdmissionId || '');

  const {
    canCreateNewAdmission,
    canInvalidateAdmission,
    canCreateNewCarePlan,
  } = useMemo(() => {
    return {
      canCreateNewAdmission:
        AdmissionRules.AdmissionRules.validateAdmissionRequest({
          activeAdmissions: admissions,
          admissionType: 'new-admission',
        }),
      canInvalidateAdmission:
        AdmissionRules.AdmissionRules.validateAdmissionRequest({
          activeAdmissions: admissions,
          admissionType: 'invalidate-admission',
        }),
      canCreateNewCarePlan: CarePlanRules.CarePlanRules.validateCarePlanRequest(
        {
          carePlans,
          rule: 'new-care-plan',
        },
      ),
    };
  }, [admissions, carePlans]);

  const handleDischarge = useCallback(
    async (data: AdmissionDischarge) => {
      await admissionApi.dischargePatient(
        patientId,
        data.statusReason,
        data.periodEnd,
      );
      await revalidate();
    },
    [patientId, revalidate],
  );

  const handleInvalidate = useCallback(
    async (data: AdmissionInvalidate) => {
      await admissionApi.invalidateAdmission(
        data.admissionId,
        data.statusReason,
      );
      await revalidate();
    },
    [revalidate],
  );

  const handleAdmissionStart = useCallback(
    async (data: AdmissionStartData) => {
      await admissionApi.startPlannedAdmission(
        data.admissionId,
        data.periodStart,
      );
      await revalidate();
    },
    [revalidate],
  );

  const handleCarePlanCreation = useCallback(
    async (data: AdmissionCarePlanCreate) => {
      if (!activeAdmission?.id) {
        return;
      }

      await carePlanApi.createCarePlan({
        admissionId: activeAdmission.id,
        patientId,
        planDefinition: data.planDefinition,
      });

      await revalidate();
    },
    [activeAdmission?.id, patientId, revalidate],
  );

  const canCreateAdmission = canCreateNewAdmission.type === 'success';
  const canInvalidate = canInvalidateAdmission.type === 'success';
  const canCreateCarePlan = canCreateNewCarePlan.type === 'success';

  const handleAction = useCallback(
    (type: AdmissionActionType, admissionId?: string) => {
      const data = getInitialActionData(type, admissionId);
      setCurrentAction({ type, data });
      setDialogOpen();
    },
    [setDialogOpen],
  );

  type ActionComponentMap = {
    discharge: React.FC<AdmissionDischargeReasonProps>;
    invalidate: React.FC<AdmissionInvalidateReasonProps>;
    createCarePlan: React.FC<CreateAdmissionCarePlanProps>;
    startAdmission: React.FC<AdmissionStartProps>;
  };

  interface ActionConfig {
    title: string;
    message: string;
    buttonText: string;
    Component: ActionComponentMap[AdmissionActionType];
    action: () => Promise<void>;
    requiredGroups: GroupSecurity[];
  }

  const actionConfigs: Record<AdmissionActionType, ActionConfig> = {
    discharge: {
      title: strings.PatientDischargeDialogTitle,
      message: strings.PatientDischargeDialogMessage,
      buttonText: strings.PatientDischargeDialogButtonText,
      Component: AdmissionDischargeReason,
      action: async () => {
        if (currentAction?.type === 'discharge') {
          await handleDischarge(currentAction.data as AdmissionDischarge);
        }
      },
      requiredGroups: [GroupSecurity.EncounterUpdate],
    },
    invalidate: {
      title: strings.AdmissionInvalidateDialogTitle,
      message: strings.AdmissionInvalidateDialogMessage,
      buttonText: strings.AdmissionInvalidate,
      Component: AdmissionInvalidateReason,
      action: async () => {
        if (currentAction?.type === 'invalidate') {
          await handleInvalidate(currentAction.data as AdmissionInvalidate);
        }
      },
      requiredGroups: [GroupSecurity.EncounterUpdate],
    },
    createCarePlan: {
      title: strings.AdmissionCarePlanCreateTitle,
      message: strings.AdmissionCarePlanDialogMessage,
      buttonText: strings.CarePlanButtonTextCreate,
      Component: CreateAdmissionCarePlan,
      action: async () => {
        if (currentAction?.type === 'createCarePlan') {
          await handleCarePlanCreation(
            currentAction.data as AdmissionCarePlanCreate,
          );
        }
      },
      requiredGroups: [GroupSecurity.CarePlanCreate],
    },
    startAdmission: {
      title: 'strings.AdmissionStartDialogTitle',
      message: 'strings.AdmissionStartDialogMessage',
      buttonText: 'strings.AdmissionStartButtonText',
      Component: AdmissionStart,
      action: async () => {
        if (currentAction?.type === 'startAdmission') {
          await handleAdmissionStart(currentAction.data as AdmissionStartData);
        }
      },
      requiredGroups: [GroupSecurity.EncounterUpdate],
    },
  };

  const handleDialogSubmit = async () => {
    if (!currentAction) return;

    await actionConfigs[currentAction.type].action();

    // Close and reset after action completes
    setDialogOpen();
    setCurrentAction(undefined);
  };

  return (
    <>
      <SkeletonProvider loading={isLoadingClinicalSummary}>
        <Section isVisible={canViewClinicalSummary}>
          <Skeleton>
            {clinicalSummary && (
              <ClinicalSummaryCard summary={clinicalSummary} />
            )}
          </Skeleton>
        </Section>
        <Section isVisible={canCreateAdmission && !isValidating}>
          <Skeleton>
            <Button
              text={strings.AdmissionButtonTextCreate}
              onPress={onCreatePress}
            />
          </Skeleton>
        </Section>

        <Section
          isVisible={canCreateCarePlan && !isValidating && !canCreateAdmission}
        >
          <Skeleton>
            <WithGroup groups={[GroupSecurity.CarePlanCreate]}>
              <Button
                text={strings.CarePlanButtonTextCreate}
                onPress={() => handleAction('createCarePlan')}
              />
            </WithGroup>
          </Skeleton>
        </Section>
      </SkeletonProvider>
      <SkeletonProvider loading={isValidating}>
        <Section>
          <Skeleton>
            <Visible if={!admissionData}>
              <Placeholder patientId={patientId} />
            </Visible>
            <AdmissionList
              admissions={admissions}
              patientId={patientId}
              canEditEncounter={canEditEncounter}
              canViewMedia={canViewMedia}
              revalidate={revalidate}
              canInvalidate={canInvalidate}
              onAction={(type, admissionId) => handleAction(type, admissionId)}
            />

            {currentAction && isDialogOpen && (
              <ConfirmationDialog
                onSubmit={handleDialogSubmit}
                buttonText={actionConfigs[currentAction.type].buttonText}
                title={actionConfigs[currentAction.type].title}
                message={actionConfigs[currentAction.type].message}
                status="status-critical"
                setDialogState={setDialogOpen}
              >
                {React.createElement(
                  actionConfigs[currentAction.type].Component as any,
                  {
                    onChange: (data: typeof currentAction.data) =>
                      setCurrentAction((prev) =>
                        prev
                          ? { type: prev.type, data: { ...prev.data, ...data } }
                          : undefined,
                      ),

                    initialData: currentAction.data,
                  },
                )}
                <Spacer height={BASE_SIZE[200]} />
              </ConfirmationDialog>
            )}
          </Skeleton>
        </Section>
      </SkeletonProvider>
    </>
  );
};
